import {FATCA_CRS_CONFIRM_KEYS} from '@sollapay/enums';
import {useEffect, useMemo, useRef} from 'react';
import {useForm} from 'react-hook-form';

import {useSignSupplementaryFatcaCrsMutation} from '@/features/trust-accounts/activation/hooks';
import {toSellerFormValues} from '@/features/trust-accounts/activation/packages/trust-details/utils';
import {createSellerEntrySchema} from '@/features/trust-accounts/activation/validation';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {
  EMPTY_SUPPLEMENTARY_FORM_VALUES,
  toFatcaCrsSupplementaryFormValues,
  toSignSupplementaryFatcaCrsPayload
} from '../utils';

import type {FormStateSnapshot} from '@/hooks';
import type {FatcaCrsDeclarationDTO, TrustSellerDetailsDTO} from '@sollapay/types';

interface UseFatcaCrsSupplementaryFormParams {
  trustAccountId: string;
  sellers: TrustSellerDetailsDTO[] | null | undefined;
  isMainDeclarationSigned: boolean;
  latestSupplementaryDeclaration: FatcaCrsDeclarationDTO | null | undefined;
  isLoading: boolean;
  isError: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

export const useFatcaCrsSupplementaryForm = ({
  trustAccountId,
  sellers,
  isMainDeclarationSigned,
  latestSupplementaryDeclaration,
  isLoading,
  isError,
  onFormStateChange
}: UseFatcaCrsSupplementaryFormParams) => {
  const signSupplementaryMutation = useSignSupplementaryFatcaCrsMutation(trustAccountId);
  const {asksSellerSideRelationshipQuestions, asksSellerSideRole} = useTrustTypeStrategy();

  const sellerEntrySchema = useMemo(
    () =>
      createSellerEntrySchema({
        asksRelationshipQuestions: asksSellerSideRelationshipQuestions,
        asksSellerRole: asksSellerSideRole
      }),
    [asksSellerSideRelationshipQuestions, asksSellerSideRole]
  );
  const isSellersReady =
    !!sellers?.length &&
    sellers.every(seller => sellerEntrySchema.safeParse(toSellerFormValues(seller)).success);

  const form = useForm({
    defaultValues: EMPTY_SUPPLEMENTARY_FORM_VALUES
  });
  const {reset} = form;

  const declarationDefaults = useMemo(
    () => toFatcaCrsSupplementaryFormValues(latestSupplementaryDeclaration),
    [latestSupplementaryDeclaration]
  );

  useEffect(() => {
    reset(declarationDefaults);
  }, [reset, declarationDefaults]);

  const handleSave = form.handleSubmit(async values => {
    await signSupplementaryMutation.mutateAsync(toSignSupplementaryFatcaCrsPayload(values));
    reset(values);
  });

  const handleCancel = () => {
    reset(declarationDefaults);
    signSupplementaryMutation.reset();
  };

  const isDirty = form.formState.isDirty;
  const isSaving = signSupplementaryMutation.isPending;

  const declarationValues = form.watch('declarationValues');
  const isLocked = !isMainDeclarationSigned || !isSellersReady;
  const hasErrors =
    isLocked ||
    !declarationValues[FATCA_CRS_CONFIRM_KEYS.BENEFICIARIES_UNCHANGED] ||
    !declarationValues[FATCA_CRS_CONFIRM_KEYS.UPDATE_COMMITMENT] ||
    !declarationValues[FATCA_CRS_CONFIRM_KEYS.SIGNATURE];

  const onSaveRef = useRef(handleSave);
  onSaveRef.current = handleSave;
  const onCancelRef = useRef(handleCancel);
  onCancelRef.current = handleCancel;

  useEffect(() => {
    onFormStateChange?.({
      isDirty,
      isLoading,
      isSaving,
      isError,
      hasErrors,
      isLocked,
      onSave: () => onSaveRef.current(),
      onCancel: () => onCancelRef.current()
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isLoading, isSaving, isError, hasErrors, isLocked]);

  return {
    form,
    handleSave,
    handleCancel,
    isDirty,
    isSaving,
    isLoading,
    isError,
    isLocked,
    saveError: signSupplementaryMutation.error
  };
};
